/**
 * Effective connectivity — reachable / degraded / unreachable.
 * Network plugin reports association only; fetch + realtime prove reachability.
 */

import {
  getFetchHealth,
  hasSustainedFetchFailure,
  noteNetworkReassociated,
  subscribeFetchHealth,
} from './fetchOutcomes';
import { getRealtimeHealth, subscribeRealtimeHealth } from '../realtime/subscriptionHealth';

export const EFFECTIVE_CONNECTIVITY = {
  REACHABLE: 'reachable',
  DEGRADED: 'degraded',
  UNREACHABLE: 'unreachable',
  UNKNOWN: 'unknown',
};

let networkAssociated = true;
let forceUnreachable = false;

const networkHintListeners = new Set();
const connectivityListeners = new Set();

function emitConnectivityChange() {
  connectivityListeners.forEach((listener) => listener());
}

/** Weak hint: interface associated (wifi/cellular). NOT proof of internet. */
export function setNetworkAssociationHint(associated) {
  const next = Boolean(associated);
  if (networkAssociated === next) return;
  const wasDisconnected = !networkAssociated && next;
  networkAssociated = next;
  if (wasDisconnected) {
    noteNetworkReassociated();
  }
  networkHintListeners.forEach((listener) => listener());
  emitConnectivityChange();
}

export function isNetworkAssociated() {
  return networkAssociated;
}

export function subscribeNetworkAssociationHint(listener) {
  networkHintListeners.add(listener);
  return () => networkHintListeners.delete(listener);
}

/** Dev/test: force unreachable to verify recovery probe (dev builds only). */
export function setForceUnreachable(value) {
  const next = Boolean(value);
  if (forceUnreachable === next) return;
  forceUnreachable = next;
  emitConnectivityChange();
}

export function isForceUnreachable() {
  return forceUnreachable;
}

function subscribeAllSignals(listener) {
  const unsubFetch = subscribeFetchHealth(listener);
  const unsubRealtime = subscribeRealtimeHealth(listener);
  const unsubNetwork = subscribeNetworkAssociationHint(listener);
  return () => {
    unsubFetch();
    unsubRealtime();
    unsubNetwork();
  };
}

/**
 * Three-state connectivity with different UX / RQ consequences.
 * - reachable: normal
 * - degraded: congested; keep queries running, no prefetch
 * - unreachable: pause RQ; sustained failure or no association
 */
export function getEffectiveConnectivity() {
  if (forceUnreachable) {
    return EFFECTIVE_CONNECTIVITY.UNREACHABLE;
  }

  if (!networkAssociated) {
    return EFFECTIVE_CONNECTIVITY.UNREACHABLE;
  }

  // Interface is up — don't latch unreachable on stale pre-reconnect failures.
  if (hasSustainedFetchFailure()) {
    return EFFECTIVE_CONNECTIVITY.DEGRADED;
  }

  const fetchHealth = getFetchHealth();
  const realtimeHealth = getRealtimeHealth();

  if (fetchHealth === 'good' || realtimeHealth === 'healthy') {
    return EFFECTIVE_CONNECTIVITY.REACHABLE;
  }

  if (fetchHealth === 'degraded' || realtimeHealth === 'unhealthy') {
    return EFFECTIVE_CONNECTIVITY.DEGRADED;
  }

  return EFFECTIVE_CONNECTIVITY.UNKNOWN;
}

export function isEffectivelyOnline() {
  const state = getEffectiveConnectivity();
  return (
    state === EFFECTIVE_CONNECTIVITY.REACHABLE ||
    state === EFFECTIVE_CONNECTIVITY.DEGRADED ||
    state === EFFECTIVE_CONNECTIVITY.UNKNOWN
  );
}

export function isEffectivelyUnreachable() {
  return getEffectiveConnectivity() === EFFECTIVE_CONNECTIVITY.UNREACHABLE;
}

export function isEffectivelyDegraded() {
  const state = getEffectiveConnectivity();
  return state === EFFECTIVE_CONNECTIVITY.DEGRADED;
}

/** Speculative prefetch only when fully reachable — not degraded or unreachable. */
export function isPrefetchAllowed() {
  return getEffectiveConnectivity() === EFFECTIVE_CONNECTIVITY.REACHABLE;
}

/** Only unreachable should pause React Query via onlineManager. */
export function shouldPauseQueriesForConnectivity() {
  return getEffectiveConnectivity() === EFFECTIVE_CONNECTIVITY.UNREACHABLE;
}

export function subscribeEffectiveConnectivity(listener) {
  connectivityListeners.add(listener);
  return () => connectivityListeners.delete(listener);
}

export { subscribeAllSignals as subscribeEffectiveConnectivityInputs };
